import type { KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SrIcon } from "@/components/ui/SrIcon";
import type { OfferLineTemplate } from "@/hooks/use-offers";
import type { OfferPhase } from "@/lib/offer-calc";
import { cn } from "@/lib/utils";
import { PHASE_OPTIONS, COST_TYPES, PRICING_TYPES, UNITS } from "./constants";

interface OfferLineTemplateRowProps {
  row: OfferLineTemplate;
  dirty: boolean;
  getValue: <K extends keyof OfferLineTemplate>(
    row: OfferLineTemplate,
    key: K,
  ) => OfferLineTemplate[K];
  patchDraft: (id: string, patch: Partial<OfferLineTemplate>) => void;
  onSave: (row: OfferLineTemplate) => void;
  /** Escape i et felt forkaster utkastet for raden. */
  onDiscard: (row: OfferLineTemplate) => void;
  onDelete: (row: OfferLineTemplate) => void;
  saving: boolean;
}

/** Enter/Escape gjelder kun tekst- og tallfelt; checkbokser og Select-knapper har egne taster. */
function isTextLikeInput(el: EventTarget | null): el is HTMLInputElement {
  return el instanceof HTMLInputElement && el.type !== "checkbox";
}

export function OfferLineTemplateRow({
  row,
  dirty,
  getValue,
  patchDraft,
  onSave,
  onDiscard,
  onDelete,
  saving,
}: OfferLineTemplateRowProps) {
  // VIB-2545: Enter lagrer (som Lagre-knappen), Escape forkaster — uten 12 Tab.
  const handleKeyDown = (e: KeyboardEvent<HTMLTableRowElement>) => {
    if (!isTextLikeInput(e.target) || !dirty) return;
    if (e.key === "Enter" && !saving) {
      e.preventDefault();
      onSave(row);
    } else if (e.key === "Escape") {
      e.preventDefault();
      onDiscard(row);
    }
  };
  return (
    <tr
      onKeyDown={handleKeyDown}
      className={cn(
        "border-b border-border/30 hover:bg-muted/20",
        !row.is_active && "opacity-50",
        dirty && "bg-amber-50/40",
      )}
    >
      <td className="py-1 px-2">
        <Input
          value={String(getValue(row, "name") ?? "")}
          onChange={(e) => patchDraft(row.id, { name: e.target.value })}
          className="h-7 text-xs"
        />
      </td>
      <td className="py-1 px-2">
        <Select
          value={String(getValue(row, "phase"))}
          onValueChange={(v) => patchDraft(row.id, { phase: v as OfferPhase })}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PHASE_OPTIONS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Select
          value={String(getValue(row, "cost_type"))}
          onValueChange={(v) => patchDraft(row.id, { cost_type: v })}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {COST_TYPES.map((c) => (
              <SelectItem key={c.value} value={c.value}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Select
          value={String(getValue(row, "pricing_type"))}
          onValueChange={(v) =>
            patchDraft(row.id, { pricing_type: v as "hourly" | "fixed" })
          }
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRICING_TYPES.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Select
          value={String(getValue(row, "default_unit") ?? "t")}
          onValueChange={(v) => patchDraft(row.id, { default_unit: v })}
        >
          <SelectTrigger className="h-7 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {UNITS.map((u) => (
              <SelectItem key={u} value={u}>
                {u}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>
      <td className="py-1 px-2">
        <Input
          type="number"
          value={String(getValue(row, "default_rate") ?? "")}
          onChange={(e) =>
            patchDraft(row.id, {
              default_rate: e.target.value === "" ? null : Number(e.target.value),
            })
          }
          // VIB-1000: piltaster/mushjul skal ikke endre satsen.
          onKeyDown={(e) => {
            if (e.key === "ArrowUp" || e.key === "ArrowDown") e.preventDefault();
          }}
          onWheel={(e) => e.currentTarget.blur()}
          className="h-7 text-xs text-right tabular-nums"
          placeholder="—"
        />
      </td>
      <td className="py-1 px-2">
        <Input
          type="number"
          step="0.25"
          value={String(getValue(row, "default_quantity") ?? "")}
          onChange={(e) =>
            patchDraft(row.id, {
              default_quantity:
                e.target.value === "" ? null : Number(e.target.value),
            })
          }
          className="h-7 text-xs text-right tabular-nums"
          placeholder="—"
        />
      </td>
      <td className="py-1 px-2">
        <Input
          value={String(getValue(row, "description") ?? "")}
          onChange={(e) =>
            patchDraft(row.id, {
              description: e.target.value === "" ? null : e.target.value,
            })
          }
          className="h-7 text-xs"
          placeholder="Valgfri beskrivelse (vises som hover-preview)"
        />
      </td>
      <td className="py-1 px-2 text-center">
        <input
          type="checkbox"
          aria-label={`Aktiv ${row.name}`}
          checked={!!getValue(row, "is_active")}
          onChange={(e) => patchDraft(row.id, { is_active: e.target.checked })}
        />
      </td>
      <td className="py-1 px-2 text-center">
        <input
          type="checkbox"
          aria-label={`Medgått ${row.name}`}
          checked={!!getValue(row, "is_medgatt")}
          onChange={(e) => patchDraft(row.id, { is_medgatt: e.target.checked })}
        />
      </td>
      <td className="py-1 px-2 text-center">
        <input
          type="checkbox"
          aria-label={`Auto-på-tilbud ${row.name}`}
          checked={!!getValue(row, "is_default_for_offers")}
          onChange={(e) =>
            patchDraft(row.id, { is_default_for_offers: e.target.checked })
          }
        />
      </td>
      <td className="py-1 px-2">
        <Input
          type="number"
          value={String(getValue(row, "sort_order") ?? 0)}
          onChange={(e) =>
            patchDraft(row.id, { sort_order: Number(e.target.value) || 0 })
          }
          className="h-7 text-xs text-right tabular-nums"
        />
      </td>
      <td className="py-1 px-2">
        <div className="flex items-center justify-end gap-1">
          {dirty && (
            <Button
              size="sm"
              variant="default"
              className="h-7 px-2 text-xs"
              onClick={() => onSave(row)}
              disabled={saving}
            >
              Lagre
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
            onClick={() => onDelete(row)}
            aria-label="Slett"
          >
            <SrIcon name="trash2" className="w-3.5 h-3.5" />
          </Button>
        </div>
      </td>
    </tr>
  );
}
